import React, { FC, useState } from 'react';

import { Button, Collapse, Group, Stack, Text, Tooltip } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import { dash } from 'radash';

import { DiagramService } from '@diagram/services/DiagramService';
import { SidebarService } from '@diagram/services/SidebarService';
import { SidebarType } from '@diagram/state/sidebar';

import { useAction } from 'hooks/use-action';
import { useCurrentProject } from 'hooks/use-current-project';
import { useCurrentUser } from 'hooks/use-current-user';
import { FeatureKey, useFeatureAccess } from 'hooks/use-feature-access';
import { TrackingService } from 'services/TrackingService';

const ExportDiagramModal: FC<ContextModalProps> = ({ id, context }) => {
    const user = useCurrentUser();
    const project = useCurrentProject();

    const hideWatermark = useFeatureAccess(FeatureKey.EXPORT_DIAGRAM_IMAGE_WITHOUT_WATERMARK);

    const handleShare = () => {
        if (user && project) {
            SidebarService.open(SidebarType.SHARE);
            context.closeModal(id);
        }
    };

    const [handleDownloadAsImage, loading] = useAction(async () => {
        let filename;

        if (project) {
            filename = `${dash(project.name.toLowerCase())}.png`;
        }

        const downloaded = await DiagramService.downloadAsPng({ filename });

        if (downloaded) {
            TrackingService.trackDownloadDiagram('png');
        }
    });

    const handleDownloadAsPdf = async () => {
        context.closeModal(id);
        await DiagramService.activatePrintMode();
    };

    return (
        <Stack>
            <Text>Did you know? You can share a view-only link to your diagram instead of exporting it.</Text>
            <Group gap="xs">
                <Button onClick={handleShare}>Share</Button>
                <Tooltip
                    label={
                        <>
                            A watermark will be added to your image on the free plan.
                            <br />
                            Upgrade your plan to remove the watermark.
                        </>
                    }
                    position="bottom"
                    disabled={hideWatermark}
                    withArrow
                >
                    <Button variant="outline" onClick={handleDownloadAsImage} loading={loading}>
                        Save as image
                    </Button>
                </Tooltip>
                <Button variant="outline" onClick={handleDownloadAsPdf}>
                    Save as pdf
                </Button>
            </Group>
        </Stack>
    );
};

export { ExportDiagramModal };
